import SolidDotIcon from '@cp/src/assets/icons/SolidDotIcon';
import { NaviTableColumnDef } from '@cp/src/components/NaviTable/interfaces';
import Typography from '@navi/web-ui/lib/primitives/Typography';
import { ExperimentStatus, ExperimentStatusMap } from './constants';
import {
  Tooltip as TooltipV2,
  TooltipContent,
  TooltipTrigger,
  Directions
} from '@cp/src/components/TooltipV2/TooltipV2';
import Button from '@navi/web-ui/lib/primitives/Button';
import { downloadDocumentFromS3WithoughtPreview } from '@cp/src/utils/DocumentDownloadFromS3';
import Download from '@cp/src/assets/images/icons/Download';
import dayjs from 'dayjs';
import { DateFormat } from '@cp/src/utils/DateHelper';
import InfoSolidSmallIcon from '@cp/src/assets/icons/InfoSolidSmallIcon';
import UploadFileIcon from '@cp/src/assets/icons/UploadFileIcon';
import { original } from '@reduxjs/toolkit';
import { OrchestrationStatus } from './interfaces';
import UploadIcon from '@cp/src/assets/images/icons/UploadIcon';
import StatusTag from './StatusTag';

export const allocationExperimentTableColumnDefs = (
  onEditClick: (experimentId: string) => void,
  onDeleteClick: (experimentName: string, experimentId: string) => void,
  onUploadClick: (experimentId: string) => void
): NaviTableColumnDef<any>[] => [
  {
    accessorKey: 'name',
    header: 'Experiment Name',
    size: 200,
    cell: ({ row }) => {
      const experimentStatus = row?.original?.status;
      return (
        <div className="flex flex-col gap-1">
          <Typography variant="h4" color="var(--grayscale-2)">
            {row?.original?.name || '-'}
          </Typography>
          {row?.original?.status ? (
            <div className="flex items-center gap-2">
              <SolidDotIcon
                fillColor={
                  experimentStatus === ExperimentStatus.ACTIVE
                    ? 'var( --green-dark)'
                    : 'var(--red-base)'
                }
              />
              <Typography variant="h6" color="var(--grayscale-cold-4)">
                {ExperimentStatusMap[row.original?.status as ExperimentStatus]}
              </Typography>
            </div>
          ) : null}
        </div>
      );
    }
  },
  {
    accessorKey: 'details',
    header: 'Details',
    size: 300,
    cell: ({ row }) => {
      return (
        <TooltipV2 placement={Directions.TOP} hideStrategy="referenceHidden">
          <TooltipTrigger tooltipTriggerClassName="tooltipTriggerWrapper">
            <Typography
              variant="h6"
              color="var(--grayscale-content-3)"
              className="line-clamp-2 max-w-[260px] text-start overflow-hidden text-ellipsis"
            >
              {row?.original?.details || '-'}
            </Typography>
          </TooltipTrigger>
          <TooltipContent className="tooltipWrapper max-w-[290px] py-2">
            {row?.original?.details}
          </TooltipContent>
        </TooltipV2>
      );
    }
  },
  {
    accessorKey: 'validEntityCount',
    header: 'Success LANs',
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-3">
          <Typography variant={'h4'} color="var(--grayscale-2)">
            {row?.original?.validEntityCount || '-'}
          </Typography>
          {row?.original?.validEntityUri ? (
            <Button
              variant="text"
              size="small"
              onClick={() =>
                downloadDocumentFromS3WithoughtPreview(
                  row?.original?.validEntityUri,
                  'Success_LANs',
                  `File download failed!`
                )
              }
            >
              <Download />
            </Button>
          ) : null}
        </div>
      );
    }
  },
  {
    accessorKey: 'invalidEntityCount',
    header: 'Failed LANs',
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-3">
          <Typography variant={'h4'} color="var(--grayscale-2)">
            {row?.original?.invalidEntityCount || '-'}
          </Typography>
          {row?.original?.errorReportUri ? (
            <Button
              variant="text"
              size="small"
              onClick={() =>
                downloadDocumentFromS3WithoughtPreview(
                  row?.original?.errorReportUri,
                  'Failed_LANs',
                  `File download failed!`
                )
              }
            >
              <Download />
            </Button>
          ) : null}
        </div>
      );
    }
  },
  {
    accessorKey: 'updatedAt',
    header: (
      <div className="flex items-center gap-2">
        {' '}
        <Typography variant="h6" color="var(--grayscale-content-3)">
          Last Updated
        </Typography>
        <TooltipV2 placement={Directions.TOP} hideStrategy="referenceHidden">
          <TooltipTrigger tooltipTriggerClassName="tooltipTriggerWrapper">
            <InfoSolidSmallIcon fill="var(--grayscale-content-3)" />
          </TooltipTrigger>
          <TooltipContent className="tooltipWrapper max-w-[280px] py-2">
            Experiments updated in this month only will be considered.
          </TooltipContent>
        </TooltipV2>
      </div>
    ),
    size: 250,
    cell: ({ row }) => {
      const date = row?.original?.updatedAt || row?.original?.createdAt;
      const updatedBy = row?.original?.updatedBy;
      const createdBy = row?.original?.createdBy;

      const isCurrentMonth = dayjs(date).isSame(dayjs(), 'month');
      const dateColor = isCurrentMonth ? 'var( --grayscale-cold-4)' : 'var(--red-base)';
      if (!updatedBy && !createdBy) return '-';
      return (
        <div className="flex flex-col justify-center gap-1">
          <Typography variant="h4" color="var(--grayscale-2)">
            {updatedBy || createdBy}
          </Typography>
          <Typography variant="h6" color={dateColor}>
            {date ? dayjs(date).format(DateFormat.DD_MMM_hh_mm_A) : '-'}
          </Typography>
        </div>
      );
    }
  },
  {
    accessorKey: 'originalFileName',
    header: 'File Name',
    size: 250,
    cell: ({ row }) => {
      const status = row?.original?.orchestrationStatus;
      if (status === OrchestrationStatus.IN_PROGRESS || status === OrchestrationStatus.FAILURE) {
        return <StatusTag orchestrationStatus={status} />;
      }
      return (
        <div className="flex items-center gap-3">
          <Button variant="text" size="small" onClick={() => onUploadClick(row?.original?.id)}>
            <div className="flex items-center gap-2">
              {row?.original?.originalFileUri ? (
                <div title={row?.original?.originalFileName} className="max-w-[150px]">
                  <Typography variant={'h4'} color="var(--grayscale-2)" className="truncate">
                    {row?.original?.originalFileName}
                  </Typography>
                </div>
              ) : (
                <Typography variant={'h4'} color="var(--blue-base)">
                  Upload
                </Typography>
              )}
              <UploadIcon />
            </div>
          </Button>
        </div>
      );
    }
  },
  {
    accessorKey: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const isDisabled = false;

      return (
        <div className="flex items-center">
          <Button
            variant="text"
            className={isDisabled ? '' : 'hover:underline'}
            disabled={isDisabled}
            onClick={() => onEditClick(row?.original?.id)}
          >
            Edit
          </Button>
          <Typography className="mx-3" color="var( --light-grayscale-border)" variant={'p4'}>
            |
          </Typography>
          <Button
            variant="text"
            className={isDisabled ? '' : 'hover:underline'}
            disabled={isDisabled}
            onClick={() => onDeleteClick(row?.original?.name, row?.original?.id)}
          >
            Delete
          </Button>
        </div>
      );
    }
  }
];
